<template>
    <article>
        <p>
            Chaos engineering involves intentionally introducing failures and faulty scenarios into distributed systems to test their resilience.
            It not only provides insights into the reliability of your system but also helps battle-test your observability stack.
            At Coroot, we leverage this approach to ensure that our product accurately identifies different failure scenarios.
        </p>

        <p>
            In this post, we'll simulate different network failures in a distributed system and see how they can be detected.
        </p>

        <h2>Lab</h2>

        <p>
            I'll be using the same demo project we use for Coroot's <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
            This demo project is a set of services that communicate with each other and various databases, accompanied by a load generator that emulates user behavior.
            In addition, <a href="https://chaos-mesh.org/" target="_blank">Chaos Mesh</a> is used for failure injection.
        </p>

        <div class="pa-5 my-6">
            <v-img max-width="1000px" class="ma-auto" src="/static/img/blog/network-issues/demo.png" />
        </div>

        <p>
            In this post, I'm going to simulate network failures between <var>front-end</var> and one of the services it communicates with.
            So, it's better to observe this at the Pod-to-Pod level.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="1000px" class="ma-auto" src="/static/img/blog/network-issues/dependency_map.png" />
        </div>

        <p>
            Now, let's introduce some chaos by cutting one of these links ✂️
        </p>


        <h2>Network partitioning</h2>

        <p>
            The Chaos Mesh spec below configures Linux's <a href="https://man7.org/linux/man-pages/man8/tc-netem.8.html" target="_blank">tc-netem</a>
            to drop all packets between <var>front-end</var> and one of the <var>catalog</var>'s pods.
        </p>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  namespace: default
  name: net-partition
spec:
  selector:
    namespaces:
      - default
    labelSelectors:
      name: catalog
    pods:
      default:
        - catalog-76f9b6f96c-22hnr
  mode: all
  action: partition
  direction: to
  target:
    selector:
      namespaces:
        - default
      labelSelectors:
        name: front-end
    mode: all
        </pre>

        <div class="pa-5 my-6">
            <v-img eager max-width="1000px" class="ma-auto" src="/static/img/blog/network-issues/partition.png" />
        </div>

        <p>
            As you can see, Coroot has detected the connectivity issue and highlighted the corresponding link on the map.
            It utilizes the <MetricLink name="container_net_latency_seconds" exporter="node-agent"/> metric, similar to how we manually detect packet loss using <var>ping</var>.
        </p>

        <p>
            To gather this metric, Coroot's agent traces all outbound connections from all containers on the node at the eBPF-level.
            Next, it measures the network Round-Trip Time (RTT) between each container and their active connection endpoints.
            This is done using ICMP probes, which help determine the time it takes for a packet to travel from the container to the destination and back.
        </p>

        <p>
            In the event of packet loss during the measurement process, the agent reports <var>NaN</var> (Not a Number) as the metric value, indicating that the measurement was not successful due to packet loss.
        </p>

        <p>
            As a result, we can easily monitor the network between any given pods.
            In our case, we can select the metric reflecting the affected connection by the <var>container_id</var> of the front-end's pod and the IP address of the catalog's pod
            (10.42.2.243)
        </p>

        <pre v-hl.bash>
container_net_latency_seconds{container_id="/k8s/default/front-end-58987ffbcd-2w9gz/front-end", destination_ip="10.42.2.243"}
        </pre>

        <div class="pa-5 my-6">
            <v-img eager max-width="1200px" class="ma-auto" src="/static/img/blog/network-issues/partition_rtt.png" />
        </div>

        <p>
            Since the agent dynamically discovers and monitors all active connections, there is no need to configure
            anything when the network topology changes.
            This is especially valuable in dynamic environments, such as Kubernetes clusters, where containers can constantly appear and disappear.
        </p>


        <h2>Network delay</h2>

        <p>
            Now, let's restore connectivity and add a delay of 50±10ms to each packet between those pods.
            As a result, the pods will be able to communicate, but the communication will be much slower than before.
        </p>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  namespace: default
  name: net-delay
spec:
  selector:
    namespaces:
      - default
    labelSelectors:
      name: catalog
    pods:
      default:
        - catalog-76f9b6f96c-22hnr
  mode: all
  action: delay
  delay:
    latency: 50ms
    correlation: '0'
    jitter: 10ms
  direction: both
  target:
    selector:
      namespaces:
        - default
      labelSelectors:
        name: front-end
    mode: all
        </pre>

        <p>
            To detect such issues, Coroot also uses the <MetricLink name="container_net_latency_seconds" exporter="node-agent"/> metric
            comparing it against a threshold (<var>10ms</var> by default).
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="1000px" class="ma-auto" src="/static/img/blog/network-issues/delay_threshold.png" />
            <v-img eager max-width="1000px" class="ma-auto" src="/static/img/blog/network-issues/delay.png" />
        </div>

        <p>
            To simplify spotting any network latency issues, Coroot consolidates its granular
            latency metrics into a Service-to-Service view.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="500px" class="ma-auto" src="/static/img/blog/network-issues/delay_chart.png" />
        </div>

        <p>
            Alternatively, the internal <a href="https://en.wikipedia.org/wiki/Transmission_Control_Protocol#Timeout-based_retransmission" target="_blank">TCP RTT</a>
            metric can be used to detect network latency issues. However, it may not always provide accurate results for several reasons:
        </p>

        <ul>
            <li>
                The TCP stack reports a calculated average or smoothed value of the TCP RTT measurements within a TCP connection.
            </li>
            <li>
                The RTT measurement can be influenced by the TCP SACK (Selective Acknowledgments) mechanism.
                In such cases, the RTT reflects the transmission of multiple segments and cannot be considered as a true round-trip time.
            </li>
        </ul>


        <h2>Packet loss</h2>

        <p>
            As you may have noticed, the previous two scenarios can be easily detected using a relatively straightforward metric.
            However, detecting partial failures, such as partial network packet loss, poses a greater challenge in real-world situations.
        </p>

        <p>
            Let's simulate a scenario with a 30% packet loss and explore how this failure can be identified.
        </p>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  namespace: default
  name: packet-loss-30
spec:
  selector:
    namespaces:
      - default
    labelSelectors:
      name: catalog
    pods:
      default:
        - catalog-76f9b6f96c-22hnr
  mode: all
  action: loss
  loss:
    loss: '30'
    correlation: '0'
  direction: to
  target:
    selector:
      namespaces:
        - default
      labelSelectors:
        name: front-end
    mode: all
        </pre>


        <p>
            Since only a portion of the packets are dropped in this scenario, it may not significantly impact the <var>container_net_latency_seconds</var> metric.
            This is because statistically, ICMP echo packets may remain unaffected.
        </p>


        <div class="pa-5 my-6">
            <v-img eager max-width="500px" class="ma-auto" src="/static/img/blog/network-issues/packet_loss_latency_chart.png" />
        </div>

        <p>
            By default, Coroot's agent performs one ICMP probe per connection during each Prometheus scrape, e.g. once every 15 seconds.
            Although it's possible to increase the frequency of these probes, doing so does not ensure 100% detection of packet loss.
        </p>

        <p>
            Let's review the processes that occur in the TCP/IP stack when packets are lost.
            One crucial mechanism in such cases is packet retransmission.
            Therefore, monitoring the number of retransmitted TCP segments can help identify partial packet loss.
        </p>

        <p>
            Additionally, unlike surrogate ICMP probes, this metric does not rely on probability.
            Instead, it provides a comprehensive overview of what happened with actual TCP connections.
        </p>

        <p>
            However, the Linux kernel provides only a global counter of retransmitted TCP segments.
        </p>

        <pre v-hl.bash>
$ netstat -s |grep -i retransmitted
    24851 segments retransmitted
        </pre>

        <p>
            Having this metric broken down by container and connection direction would indeed be incredibly valuable.
            Thanks to eBPF, it's now possible to extract any required information directly from the kernel.
            Coroot's agent gathers such a <a href="/docs/metric-exporters/node-agent/metrics#container_net_tcp_retransmits_total" target="_blank">metric</a> by using the <var>tcp:tcp_retransmit_skb</var> kernel tracepoint.
        </p>

        <p>
            Returning to our failure scenario, by examining the number of TCP retransmissions broken down by upstream services,
            we can easily identify the issue.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="500px" class="ma-auto" src="/static/img/blog/network-issues/packet_loss_retrans_chart.png" />
        </div>

        <p>
            Furthermore, the TCP retransmission mechanism is also triggered in the event of out-of-order packets or when there
            is insufficient bandwidth available for transmitting or receiving packets.
        </p>

        <h2>Conclusion</h2>

        <p>
            As you can see, it's possible to identify all the network attack types provided by Chaos Mesh using just two fine granular metrics.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="600px" class="ma-auto" src="/static/img/blog/network-issues/network_attacks.png" />
        </div>

        <p>
            It's worth mentioning that most observability tools typically offer only network interface metrics,
            which are entirely useless when it comes to diagnosing network failures.
            Perhaps this is because no one has tried to test these tools on real failure scenarios 😉
        </p>

        <p>
            At Coroot, we leverage Chaos Engineering to ensure that our product can help you quickly pinpoint the
            root causes of even the most complex failures.
        </p>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            Not ready to Get started with Coroot? Check out our <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
        </p>

        <p>If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ or share your experience on
            <a href="https://www.g2.com/products/coroot/reviews" target="_blank">G2</a>.</p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>

import MetricLink from "@/components/MetricLink.vue";

export default {
    components: {MetricLink},
}
</script>

<style scoped>
.marked {
    font-weight: 600;
    color: #F98D88;
}

.comment {
    font-weight: 600;
    color: white;
}
</style>
